import { Vector2d } from 'konva/lib/types';
import {
  CollectionState,
  DimensionsInfo,
  Frame,
  RelativeVector2d,
  Vector2dTuple,
} from '@/entities/collection';
import {
  Collection,
  collectionApiInstance,
  CreateImageVariables,
  FrameImage,
} from '@/shared/api';
import { VIDEO_ASPECT_RATIO } from '@/shared/lib';

export const buildImageSrc = (imageId: string) => {
  const baseURL = collectionApiInstance.defaults.baseURL;
  return `${baseURL}/captured_images/${imageId}`;
};

export const buildFrame = (
  state: CollectionState,
  pngImagePath: string,
  webpImagePath: string,
  backgroundImageId: string,
  topLeft: RelativeVector2d,
  bottomRight: RelativeVector2d
): Frame => {
  const isSingleSkuWithoutPoint =
    state.mode === 'single' &&
    (Object.keys(state.skuToPoint).length === 0 ||
      state.skuToPoint[0] === null);

  const variants = isSingleSkuWithoutPoint
    ? state.skuList
    : state.skuList.filter((_, index) => !!state.skuToPoint[index]);

  return {
    id: null,
    image: buildImageSrc(webpImagePath),
    imageId: pngImagePath,
    backgroundImage: buildImageSrc(backgroundImageId),
    backgroundId: backgroundImageId,
    variants,
    points: state.skuToPoint,
    capturedAt: new Date().toISOString(),
    polygonFinderParameters: {
      topLeft: topLeft,
      bottomRight: bottomRight,
    },
  };
};

export const parseCreatedAt = (name: string) =>
  new Date(name.split('_')[1]).getTime();

export const sortCollections = (collections: Collection[]) => {
  return [...collections].sort(
    (a, b) => parseCreatedAt(b.name) - parseCreatedAt(a.name)
  );
};

export const sortImages = (images: FrameImage[]) => {
  return [...images].sort(
    (a, b) =>
      new Date(a.capture_date).getTime() - new Date(b.capture_date).getTime()
  );
};

export const vectorToTuple = ({ x, y }: Vector2d): Vector2dTuple => [x, y];
export const upscaleRelativeVector =
  (dimensions: DimensionsInfo) =>
  ({ relativeX, relativeY }: RelativeVector2d): Vector2dTuple =>
    [
      Math.floor(relativeX * dimensions.videoWidth),
      Math.floor(relativeY * dimensions.videoHeight),
    ];

export const upscalePoint = (
  point: Vector2dTuple,
  { canvasWidth, videoWidth, videoHeight }: DimensionsInfo,
  aspectRatio = VIDEO_ASPECT_RATIO
): Vector2dTuple => [
  Math.floor(videoWidth * (point[0] / canvasWidth)),
  Math.floor((videoHeight * point[1] * aspectRatio) / canvasWidth),
];

export const transfromImageIntoFrame =
  ({ videoWidth, videoHeight, canvasWidth }: DimensionsInfo) =>
  (image: FrameImage): Frame => ({
    id: image.id,
    imageId: image.image_id,
    image: buildImageSrc(image.image_id),
    backgroundId: image.background_image_id,
    backgroundImage: buildImageSrc(image.background_image_id),
    variants: image.variants,
    points: image.variants.reduce<Frame['points']>((acc, _, index) => {
      const point = image.points[index];
      if (!point) return acc;

      const [x, y] = point;

      acc[index] = {
        x: (x / videoWidth) * canvasWidth,
        y: ((y / videoHeight) * canvasWidth) / VIDEO_ASPECT_RATIO,
        relativeX: x / videoWidth,
        relativeY: y / videoHeight,
      };

      return acc;
    }, []),
    capturedAt: image.capture_date,
    polygonFinderParameters: {
      topLeft: {
        x: image.polygon_finder_parameters.top_left[0] * canvasWidth,
        y:
          image.polygon_finder_parameters.top_left[1] *
          (canvasWidth / VIDEO_ASPECT_RATIO),
        relativeX: image.polygon_finder_parameters.top_left[0],
        relativeY: image.polygon_finder_parameters.top_left[1],
      },
      bottomRight: {
        x: image.polygon_finder_parameters.bottom_right[0] * canvasWidth,
        y:
          image.polygon_finder_parameters.bottom_right[1] *
          (canvasWidth / VIDEO_ASPECT_RATIO),
        relativeX: image.polygon_finder_parameters.top_left[0],
        relativeY: image.polygon_finder_parameters.top_left[1],
      },
    },
  });

export const transformFrameIntoImage = (
  collectionId: string,
  frame: Frame,
  dimensions: DimensionsInfo
): CreateImageVariables => {
  const variant_ids = frame.variants.map((variant) => variant.id);
  const points = Object.values(frame.points).filter(
    (item) => item !== null
  ) as RelativeVector2d[];
  const upscaledPoints = points.map(upscaleRelativeVector(dimensions));

  return {
    collection_id: collectionId,
    image_id: frame.imageId,
    background_image_id: frame.backgroundId,
    variant_ids: variant_ids,
    points: upscaledPoints,
    capture_date: frame.capturedAt,
    polygon_finder_parameters: {
      top_left: [
        frame.polygonFinderParameters.topLeft.relativeX,
        frame.polygonFinderParameters.topLeft.relativeY,
      ],
      bottom_right: [
        frame.polygonFinderParameters.bottomRight.relativeX,
        frame.polygonFinderParameters.bottomRight.relativeY,
      ],
    },
  };
};
